import React, { useContext } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { validAsciiChar } from "../../Constants/utility";
import { KBContext, SearchContext } from "../../Context";
import { Label } from "../StyledComponents/StyledComponents";
import searchIcon from "../../images/searchIcon.svg";
import closeIcon from "../../images/closeIcon.svg";
import { GMAIL_SEARCH_PLACEHOLDER, OMNI_SEARCH_PLACEHOLDER, OMNI_PRODUCT_TYPE, OUTLOOK_PRODUCT_TYPE, SEARCH_PLACEHOLDER } from "../../Constants/KBContext.constants";
import { isFeatureEnabled } from "../../utils";
import { FTR_KB_FOR_OUTLOOK, PRODUCT_KB_ORIGIN } from "../../Constants";
import ButtonLoader from "../Loaders/ButtonLoader";
import { sendGainsightEvent, KB_WEBSITE_GAINSIGHT_EVENTS } from "../../gainSightIntegration";

const Image_Base_Url = process.env.REACT_APP_IMAGE_BASE_URL;

function getBannerBackgroundImage(bannerData) {
  const img = bannerData?.bannerImage;
  const base = Image_Base_Url?.trim?.() || "";
  if (!img || !base) return "none";
  const baseWithSlash = base.endsWith("/") ? base : `${base}/`;
  const imgPath = img.startsWith("/") ? img.slice(1) : img;
  return `url(${baseWithSlash}${imgPath})`;
}

const WrapperContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.pathname === "/" ? "center" : "")};
  justify-content: center;
  align-items: center;

  width: 100%;
  box-sizing: border-box;
  padding: 0 180px;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media screen and (max-width: 1100px) {
    padding: 0 90px;
  }

  @media screen and (max-width: 800px) {
    padding: 0 50px;
  }

  @media screen and (max-width: 500px) {
    padding: 0 24px;
  }

  transition: padding 1s;
`;

const BannerWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  height: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue"
      ? "342px"
      : "240px"};

  background-color: ${(props) =>
    props.bannerData?.bannerImage ? "transparent" : props.bannerData?.brandColor || ""};
  background-image: ${(props) => getBannerBackgroundImage(props.bannerData)};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  @media (max-width: 700px) {
    height: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue"
      ? "282px"
      : "118px"};
  }
  overflow: hidden;
  
  transition: height 1s;
`;

const BannerInput = styled.input`
  --banner-input-height: 64px;
  box-sizing: border-box;
  width: 750px;
  outline: none !important;
  height: var(--banner-input-height);
  border: 0px;
  border-radius: calc(var(--banner-input-height) / 2);
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 31px;
  letter-spacing: -0.320374071598053px;
  text-align: left;
  padding: 0 16px 0 65px;
  background: url(${searchIcon}) no-repeat;
  background-position: ${(props) =>
    props.pathname === "/" ? "2.5% 22px" : "2.5% 18px"};
  background-color: white;

  ::placeholder { 
   color: #8F97B2;
  }

  @media screen and (max-width: 1100px) {
    width: -webkit-fill-available;
  }

  @media screen and (max-width: 700px) {
    width: -webkit-fill-available;
    font-weight: 400;
    font-size: 20px;
    line-height: 24px;

    background-size: 24px 24px;

    height: 54px;
    background-position: 16px 16px;
    margin: 0;
    padding: 0 18px 0 56px;
  }

  @media screen and (max-width: 500px) {
    width: -webkit-fill-available;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;

    background-size: 20px 20px;

    height: 54px;
    background-position: 16px 16px;
    margin: 0;
    padding: 0 16px 0 48px;
  }

  transition: 1s;
`;

const AbsoluteWrapper = styled.div`
  width: ${(props) => props.width || "100%"};
  position: ${(props) => props.position};
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  right: ${(props) => props.top};
  bottom: ${(props) => props.left};
  z-index: ${(props) => props.zIndex};
  transform: ${(props) => props.transform};
  cursor: ${(props) => props.cursor};

  @media screen and (max-width: 1100px) {
    width: ${(props) => props.search === true && "fit-content"};
    width: ${(props) => props.pathname !== "/" && "100%"};
    top: ${(props) => (props.pathname === "/" ? props.top : "15px")};
  }

  @media screen and (max-width: 700px) {
    top: 15px;
  }

  @media screen and (max-width: 500px) {
    top: ${(props) => (props.pathname === "/" ? "14px" : "15px")};
  }

  transition: 1s;
`;

const HoverAbsolute = styled(AbsoluteWrapper)`
  display: flex;
  height: 30px;
  width: 30px !important;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  &:hover {
    background: rgb(245, 245, 245);
  }
`;

export default function LegacyBannerSection({ bannerData }) {
  const { setSearchInput, searchInput, setSearchList, triggerSearch, loading } =
    useContext(SearchContext);
  const { availableFeatures } = useContext(KBContext);

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const product = searchParams.get("product");
  let searchPlaceholder = "Search";
  if (isFeatureEnabled(FTR_KB_FOR_OUTLOOK, availableFeatures) && window.origin === PRODUCT_KB_ORIGIN) {
    searchPlaceholder = SEARCH_PLACEHOLDER.replace("{{product}}", [OUTLOOK_PRODUCT_TYPE, OMNI_PRODUCT_TYPE].includes(product) ? OMNI_SEARCH_PLACEHOLDER : GMAIL_SEARCH_PLACEHOLDER);
  }

  const handleBannerInputChange = (e) => {
    if (validAsciiChar(e.target.value)) {
      setSearchInput(e.target.value);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && searchInput.length > 0) {
      triggerSearch();
    }
  };

  const handleSearchClickEvent = () => {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_INITIATED);
  };

  return (
    <BannerWrapper pathname={pathname} bannerData={bannerData}>
      <WrapperContent pathname={pathname}>
        {pathname.slice(1) === "" && (
          <Label
            padding={pathname.slice(1) === "submit-issue" && "0"}
            responsive
            bannerColor={bannerData.bannerTextColor}
            className="bannerText"
          >
            {bannerData.bannerText}
          </Label>
        )}

        {pathname.slice(1) === "submit-issue" && (
          <Label
            padding={pathname.slice(1) === "submit-issue" && "0"}
            responsive
            bannerColor={bannerData.bannerTextColor}
            className="bannerText"
          >
            {bannerData.helpPageHeading}
          </Label>
        )}

        {pathname.slice(1) === "submit-issue" ? (
          ""
        ) : (
          <>
            <AbsoluteWrapper
              position="relative"
              search={searchInput.length > 0}
              width={pathname === "/" ? "auto" : "fit-content"}
            >
              <BannerInput
                bannerLeft={pathname.slice(1) === ""}
                pathname={pathname}
                type="text"
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={handleBannerInputChange}
                onKeyDown={handleKeyDown}
                onClick={handleSearchClickEvent}
              />
              {searchInput.length > 0 && (
                <HoverAbsolute
                  position="absolute"
                  width="auto !important"
                  cursor="pointer"
                  pathname={pathname}
                  right="20px"
                  top={pathname === "/" ? "20px" : "17px"}
                  onClick={() => {
                    setSearchInput("");
                    setSearchList(false);
                  }}
                >
                  {
                    loading ? <ButtonLoader h="20px" w="20px" /> :
                      <img
                        style={{ zIndex: 10, height: 20, width: 20 }}
                        src={closeIcon}
                        alt=""
                      />
                  }
                </HoverAbsolute>
              )}
            </AbsoluteWrapper>
          </>
        )}
      </WrapperContent>
    </BannerWrapper>
  );
}
